import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import SEOHead from '../components/SEOHead';

export default function HealthCheck() {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Check backend health
        const response = await fetch('/api/health');
        const data = await response.json();
        
        setHealthData({
          backend: data,
          frontend: {
            status: 'healthy',
            timestamp: new Date().toISOString(),
            environment: process.env.NODE_ENV || 'development',
            user_agent: navigator.userAgent,
            url: window.location.href,
            checks: {
              react: {
                status: 'healthy',
                message: 'React application is running'
              },
              api_connection: {
                status: response.ok ? 'healthy' : 'error',
                message: response.ok ? 'Successfully connected to backend' : 'Failed to connect to backend'
              }
            }
          }
        });
      } catch (err) {
        setError(err.message);
        setHealthData({
          backend: null,
          frontend: {
            status: 'healthy',
            timestamp: new Date().toISOString(),
            environment: process.env.NODE_ENV || 'development',
            user_agent: navigator.userAgent,
            url: window.location.href,
            checks: {
              react: {
                status: 'healthy',
                message: 'React application is running'
              },
              api_connection: {
                status: 'error',
                message: `Failed to connect to backend: ${err.message}`
              }
            }
          }
        });
      } finally {
        setLoading(false);
      }
    };

    checkHealth();
    
    // Refresh health check every 30 seconds
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'degraded':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Loader2 className="h-5 w-5 text-gray-500 animate-spin" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'healthy':
        return <Badge variant="default" className="bg-green-500">Healthy</Badge>;
      case 'degraded':
        return <Badge variant="secondary" className="bg-yellow-500">Degraded</Badge>;
      case 'error':
        return <Badge variant="destructive">Error</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold">Checking System Health...</h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEOHead 
        title="System Health Status | Grow & Stay Rich"
        description="Live system diagnostics and status for Grow & Stay Rich web application and backend API services."
        path="/health"
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">System Health Check</h1>
          <p className="text-gray-600">Monitor frontend and backend status</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Frontend Health */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Frontend Status</span>
                {getStatusIcon(healthData?.frontend?.status)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Overall Status:</span>
                  {getStatusBadge(healthData?.frontend?.status)}
                </div>
                <div className="text-sm text-gray-600">
                  <p><strong>Environment:</strong> {healthData?.frontend?.environment}</p>
                  <p><strong>Timestamp:</strong> {new Date(healthData?.frontend?.timestamp).toLocaleString()}</p>
                  <p><strong>URL:</strong> {healthData?.frontend?.url}</p>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-medium">Component Checks:</h4>
                  {Object.entries(healthData?.frontend?.checks || {}).map(([key, check]) => (
                    <div key={key} className="flex items-center justify-between text-sm">
                      <span className="capitalize">{key.replace('_', ' ')}:</span>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(check.status)}
                        <span className="text-gray-600">{check.message}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Backend Health */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Backend Status</span>
                {healthData?.backend ? getStatusIcon(healthData.backend.status) : getStatusIcon('error')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {healthData?.backend ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Overall Status:</span>
                    {getStatusBadge(healthData.backend.status)}
                  </div>
                  <div className="text-sm text-gray-600">
                    <p><strong>Environment:</strong> {healthData.backend.environment}</p>
                    <p><strong>Version:</strong> {healthData.backend.version}</p>
                    <p><strong>Uptime:</strong> {Math.round(healthData.backend.uptime)}s</p>
                    <p><strong>Response Time:</strong> {healthData.backend.response_time}</p>
                    <p><strong>Memory:</strong> {healthData.backend.checks?.memory?.used}MB / {healthData.backend.checks?.memory?.total}MB</p>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium">Service Checks:</h4>
                    {Object.entries(healthData.backend.checks || {}).map(([key, check]) => (
                      <div key={key} className="flex items-center justify-between text-sm">
                        <span className="capitalize">{key.replace('_', ' ')}:</span>
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(check.status)}
                          <span className="text-gray-600">{check.message}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <XCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                  <p className="text-red-600">Backend Unavailable</p>
                  {error && <p className="text-sm text-gray-500 mt-1">{error}</p>}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Last Updated */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Last updated: {new Date().toLocaleString()}</p>
          <p className="mt-1">Auto-refreshes every 30 seconds</p>
        </div>
      </div>
    </div>
  );
}
